// The company logo set by hand, on the record's head (pipeline RC4; U359, U371): a person who may edit the record can set
// or replace the logo with an image of their own, and take a hand-set one off again, through pipeline RC7's route
// (track B: PUT and DELETE /api/records/companies/:id/logo). RC7's nightly fetch never overwrites a hand-set logo. The
// control shows only where the read carries RC7's logo fields, so it never offers a route the build does not have.
import { ActionInput, ActionButton } from '../parts/index.js';
import { useRef, useState } from 'react';
import { records, type CompanyLogo } from '../api';

export const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
export const LOGO_MAX_BYTES = 512 * 1024;

/** Why an image cannot be a logo, in words, before it is sent; the server checks the bytes again (RC7). */
export function logoRefusal(file: { type: string; size: number }): string | null {
  if (!LOGO_TYPES.includes(file.type)) return 'A logo is a PNG, JPEG, WebP or GIF image.';
  if (file.size > LOGO_MAX_BYTES) return `That image is ${Math.ceil(file.size / 1024).toLocaleString('en-GB')} KB; a logo is at most 512 KB.`;
  return null;
}

const asDataUrl = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result));
  r.onerror = () => reject(new Error('the image could not be read'));
  r.readAsDataURL(file);
});

export function LogoControl({ storeId, companyId, logo, onLogo }: { storeId: string; companyId: string; logo: CompanyLogo | null; onLogo: (logo: CompanyLogo | null) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [wrong, setWrong] = useState<string | null>(null);
  const said = (e: unknown) => `Not saved: ${e instanceof Error ? e.message.replace(/\.$/, '') : String(e)}.`;
  const choose = async (file: File | undefined) => {
    if (!file) return;
    const no = logoRefusal(file);
    if (no) { setWrong(no); return; }
    setBusy(true); setWrong(null);
    try { onLogo((await records.setLogo(storeId, companyId, await asDataUrl(file))).logo); } catch (e) { setWrong(said(e)); } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };
  const takeOff = async () => {
    setBusy(true); setWrong(null);
    try { onLogo((await records.clearLogo(storeId, companyId)).logo); } catch (e) { setWrong(said(e)); } finally { setBusy(false); }
  };
  return (
    <span className="alogo">
      <ActionInput ref={input} type="file" accept={LOGO_TYPES.join(',')} hidden onChange={(e) => void choose(e.target.files?.[0])} />
      <ActionButton type="button" className="hx-link" disabled={busy} onClick={() => input.current?.click()}>
        {busy ? 'Saving the logo' : logo ? 'Replace the logo' : 'Set the logo'}
      </ActionButton>
      {logo?.source === 'hand' && !busy
        ? <ActionButton type="button" className="hx-link" onClick={() => void takeOff()}>Take off the logo set by hand</ActionButton>
        : null}
      {wrong ? <span className="awrong" role="alert">{wrong}</span> : null}
    </span>
  );
}
